import React, { useState, useEffect, useRef, useMemo, useContext, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import PropTypes from 'prop-types';

import ApiContext from '@/contexts/ApiContext';
import containerClasses from '@/orderForm/style.module.scss';
import OrderIcon from '@/components/OrderIcon/OrderIcon';
import UserContext from '@/contexts/UserContext';
import { getBryEnvelopeConfig } from '@/helpers/openBryEnvelopeConfig';
import Loading from '@/Loading';
import FormError from '@/orderForm/FormError';
import NoAvailablePackages from '@/orderForm/NoAvailablePackages';
import OrderForm from '@/orderForm/OrderForm/OrderForm';
import OrderFormPageFooter from '@/orderForm/OrderForm/OrderFormPage/OrderFormPageFooter';
import OrderFormPageHeader from '@/orderForm/OrderForm/OrderFormPage/OrderFormPageHeader';
import PackageConsumption from '@/orderForm/PackageConsumption';
import GenericWarning from '@/orderForm/GenericWarning';
import UnproductiveWarning from '@/orderForm/UnproductiveWarning';
import { useGetOrderFormNew, useOrderFormMutations } from '@/orderForm/hooks/useOrderForm';
import deepMerge from '@/orderForm/utils/deepMerge';
import { generateUiSchema } from '@/orderForm/utils/uiSchemaGenerator';
import { showToast as dispatchToastAction } from '@/store/ducks/toasts';

const CLIENT_RESTRICTED_FRANCHISEE_STEP_TYPES = [
  'entrada',
  'saida',
  'aditivo',
  'conferencia',
  'transferencia',
  'fotosDeCaptacao',
  'auditoriaVilaTrampolim',
  'finalDeObraBoticario',
  'cacaVazamento',
];

const SKIP_CONFIRMATION_ORDER_TYPES = ['auditoriaVilaTrampolim'];

const isSingleStepOrderType = orderType =>
  orderType?.startsWith('marketplace_') ||
  orderType === 'visitaDigital' ||
  orderType === 'fotosDeCaptacao' ||
  orderType === 'cacaVazamento';

const hiddenObjectField = childKey => ({
  'ui:widget': 'hidden',
  'ui:hidden': true,
  ...(childKey ? { [childKey]: { 'ui:widget': 'hidden', 'ui:hidden': true } } : {}),
});

const removeSchemaProperties = (schema, keys) => {
  if (!schema || !Array.isArray(keys)) return;

  keys.forEach(key => {
    if (schema.properties) delete schema.properties[key];
    if (schema.definitions) delete schema.definitions[key];
  });

  if (Array.isArray(schema.required)) {
    schema.required = schema.required.filter(key => !keys.includes(key));
  }
};

// Extract default values from JSON Schema
const buildDefaultsFromSchema = schema => {
  if (!schema || typeof schema !== 'object') return {};
  const properties = schema.properties || {};
  const defaults = {};

  const ensureDefault = node => {
    if (!node || typeof node !== 'object') return undefined;
    if (node.default !== undefined) return node.default;
    const nodeType = Array.isArray(node.type) ? node.type[0] : node.type;

    if (nodeType === 'object') {
      const childProps = node.properties || {};
      const obj = {};
      for (const key of Object.keys(childProps)) {
        const child = childProps[key];
        const childDefault = ensureDefault(child);

        if (childDefault !== undefined) {
          obj[key] = childDefault;
        }
      }
      return obj;
    }

    if (nodeType === 'array' && node.minItems > 0) {
      const itemDefault = node.items ? ensureDefault(node.items) || {} : {};
      return Array(node.minItems).fill(itemDefault);
    }

    return undefined;
  };

  for (const key of Object.keys(properties)) {
    const node = properties[key];
    const defVal = ensureDefault(node);

    if (defVal !== undefined) {
      defaults[key] = defVal;
    }
  }

  return defaults;
};

const cleanNulls = obj => {
  if (obj === null || obj === undefined) return undefined;
  if (typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) {
    return obj.map(item => cleanNulls(item));
  }
  const cleaned = {};
  for (const key of Object.keys(obj)) {
    const value = cleanNulls(obj[key]);
    if (value !== null && value !== undefined) {
      cleaned[key] = value;
    }
  }
  return cleaned;
};

const getAllSchemaProperties = schema => {
  if (!schema || typeof schema !== 'object') return [];
  let props = [];
  if (schema.properties) {
    props = props.concat(Object.keys(schema.properties));
  }
  if (schema.dependencies) {
    Object.values(schema.dependencies).forEach(dep => {
      if (dep && typeof dep === 'object') {
        if (dep.properties) {
          props = props.concat(Object.keys(dep.properties));
        }
        if (Array.isArray(dep.oneOf)) {
          dep.oneOf.forEach(subSchema => {
            props = props.concat(getAllSchemaProperties(subSchema));
          });
        }
      }
    });
  }
  return [...new Set(props)];
};

const resolveRef = (rootSchema, node) => {
  if (!node?.$ref) return node;
  const defName = node.$ref.split('/').pop();
  return rootSchema?.definitions?.[defName] || node;
};

const findSchemaNodeForProperty = (schema, key, rootSchema = schema) => {
  if (!schema || typeof schema !== 'object') return null;

  // 1. Check direct properties
  if (schema.properties && schema.properties[key]) {
    return resolveRef(rootSchema, schema.properties[key]);
  }

  // 2. Check dependencies
  if (schema.dependencies) {
    for (const dep of Object.values(schema.dependencies)) {
      if (dep && typeof dep === 'object') {
        if (dep.properties && dep.properties[key]) {
          return resolveRef(rootSchema, dep.properties[key]);
        }
        if (Array.isArray(dep.oneOf)) {
          for (const subSchema of dep.oneOf) {
            const found = findSchemaNodeForProperty(subSchema, key, rootSchema);
            if (found) return resolveRef(rootSchema, found);
          }
        }
      }
    }
  }

  // 3. Check anyOf / oneOf / allOf
  for (const field of ['oneOf', 'anyOf', 'allOf']) {
    if (Array.isArray(schema[field])) {
      for (const subSchema of schema[field]) {
        const found = findSchemaNodeForProperty(subSchema, key, rootSchema);
        if (found) return resolveRef(rootSchema, found);
      }
    }
  }

  return null;
};

const sanitizeUiSchemaForSchema = (inputUiSchema = {}, inputSchema = {}, rootSchema = inputSchema) => {
  if (!inputUiSchema || typeof inputUiSchema !== 'object' || Array.isArray(inputUiSchema)) {
    return inputUiSchema;
  }

  const allPossibleProps = getAllSchemaProperties(inputSchema);
  const sanitized = {};

  Object.keys(inputUiSchema).forEach(key => {
    if (key.startsWith('ui:')) {
      sanitized[key] = inputUiSchema[key];
    }
  });

  if (Array.isArray(sanitized['ui:order'])) {
    sanitized['ui:order'] = sanitized['ui:order'].filter(key => key === '*' || allPossibleProps.includes(key));
  }

  Object.keys(inputUiSchema).forEach(key => {
    if (key.startsWith('ui:')) return;

    const schemaNode = findSchemaNodeForProperty(inputSchema, key, rootSchema);
    if (!schemaNode) return;

    const uiNode = inputUiSchema[key];
    const isNestedObject =
      !!schemaNode &&
      typeof schemaNode === 'object' &&
      !Array.isArray(schemaNode) &&
      (!!schemaNode.properties || schemaNode.type === 'object' || !!schemaNode.$ref);

    sanitized[key] = isNestedObject ? sanitizeUiSchemaForSchema(uiNode, schemaNode, rootSchema) : uiNode;
  });

  return sanitized;
};

const OrderFormPage = ({
  cancelNewOrder,
  inspectionIcon,
  newOrderType,
  previousOrderId,
  previousOrderFile,
  previousPropertyId,
  successfulOrderCreation,
  updateOrderItem,
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const me = useContext(UserContext);

  const isClient = useMemo(() => {
    const roleCandidates = [
      me?.authRole,
      me?.role,
      me?.auth_role,
      me?.profile?.role,
      me?.profile?.authRole,
      me?.profile?.auth_role,
    ].filter(Boolean);

    return roleCandidates.some(role => {
      const normalized = String(role).toUpperCase().trim();
      return normalized === 'ROLE_CLIENT' || normalized === 'CLIENT';
    });
  }, [me]);

  const [searchParams] = useSearchParams();
  const reportHash = searchParams.get('report_hash');

  const formRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const lastPriceParamsRef = useRef(null);
  const orderCreationSucceededRef = useRef(false);

  const [currentStep, setCurrentStep] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState('');
  const [newOrderStatus, setNewOrderStatus] = useState('SAVE_ORDER');
  const [errors, setErrors] = useState(0);
  const [formData, setFormData] = useState({});
  const [packageConsume, setPackageConsume] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [unproductiveWarningAccepted, setUnproductiveWarningAccepted] = useState(false);
  const [genericWarningAccepted, setGenericWarningAccepted] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [isFileUploading, setIsFileUploading] = useState(false);

  const { data: order, isLoading: isQueryLoading } = useGetOrderFormNew(
    newOrderType,
    previousOrderId,
    previousPropertyId,
    reportHash
  );
  const mutations = useOrderFormMutations();

  const processedSchema = useMemo(() => {
    if (!order?.schema) return null;
    const schema = JSON.parse(JSON.stringify(order.schema));

    if (schema.properties && schema.definitions) {
      Object.keys(schema.properties).forEach(key => {
        const prop = schema.properties[key];
        if (prop && prop.$ref) {
          const refName = prop.$ref.split('/').pop();
          if (schema.definitions[refName]) {
            schema.properties[key] = {
              ...schema.definitions[refName],
            };
          }
        }
      });
    }

    const isMarketplace = String(newOrderType || '').startsWith('marketplace_');
    const hasBudgetDuringInspectionSectionAllowed =
      me?.features?.includes('FORM_BUDGET_DURING_INSPECTION') && newOrderType === 'saida';

    if (!hasBudgetDuringInspectionSectionAllowed) {
      removeSchemaProperties(schema, ['budgetDuringInspection']);
    }

    if (newOrderType === 'acompanhamentoObraMM') {
      removeSchemaProperties(
        schema,
        isClient
          ? ['additionals', 'accessInformation']
          : ['keys', 'cost', 'additionals', 'accessInformation', 'accompaniedInspection', 'recommendation']
      );
    } else {
      removeSchemaProperties(schema, ['construction_details', 'solicitations']);
    }

    if (newOrderType === 'cautelarQueirozGalvao') {
      removeSchemaProperties(schema, ['accessInformation', 'accompaniedInspection']);
    } else {
      removeSchemaProperties(schema, ['location_references']);
    }

    // Preserve price fields for clients (skip for marketplace as they have strict schemas)
    if (isClient && schema.properties && !isMarketplace) {
      if (!schema.properties.details) schema.properties.details = { type: 'object', properties: {} };
      if (schema.properties.details.properties) {
        schema.properties.details.properties.price = schema.properties.details.properties.price || {
          type: 'number',
          title: 'price',
        };
      }
      if (!schema.properties.additionalCosts) schema.properties.additionalCosts = { type: 'object', properties: {} };
    }

    if (schema.additionalProperties === false) {
      schema.additionalProperties = true;
    }

    // Allow metadata fields (skip for marketplace orders as they have strict schemas)
    if (schema.properties && !isMarketplace) {
      const metadata = {
        clients: { type: 'object' },
        inspection_type: { type: 'string' },
        attachments: { type: 'array' },
        details: { type: 'object' },
      };

      Object.entries(metadata).forEach(([key, value]) => {
        if (!schema.properties[key]) {
          schema.properties[key] = value;
        }
      });
    }

    return schema;
  }, [order?.schema, isClient, newOrderType, me?.features]);

  // Redirect to previous inspection selector if required reference is missing (e.g. direct access or F5 page reload)
  useEffect(() => {
    if (orderCreationSucceededRef.current || !location.pathname.startsWith('/orders/new/')) return;

    const typesRequiringPrevious = [
      'saida',
      'saidaCurtaTemporada',
      'conferencia',
      'aditivo',
      'conferenciaFinalDeObra',
      'conferenciaEntrega',
      'intermediariaBHomy',
      'revistoriaBrookfield',
    ];

    if (typesRequiringPrevious.includes(newOrderType) && !previousOrderId && !previousOrderFile) {
      if (isClient) {
        navigate(`/orders/new/${newOrderType}/previous`);
      } else {
        navigate(`/orders/new/${newOrderType}/attach`);
      }
    }
  }, [isClient, newOrderType, previousOrderId, previousOrderFile, navigate, location.pathname]);

  // Reset wizard state on type change
  useEffect(() => {
    setInitialized(false);
    setFormData({});
    setCurrentStep(0);
    setNewOrderStatus('SAVE_ORDER');
    setPackageConsume(null);
    setSelectedPackage('');
    setErrors(0);
    setErrorMessage('');
    setSubmitted(false);
    setUnproductiveWarningAccepted(false);
    setGenericWarningAccepted(false);
  }, [newOrderType, previousOrderId, previousOrderFile?.file?.remote_name, previousPropertyId, reportHash]);

  // Initialize formData from schema defaults + API payload.
  useEffect(() => {
    if (processedSchema && !initialized) {
      const schemaDefaults = buildDefaultsFromSchema(processedSchema);
      let initialData = deepMerge(schemaDefaults, cleanNulls(order.data || {}));

      const isMarketplace = String(newOrderType || '').startsWith('marketplace_');
      if (!isMarketplace) {
        if (previousOrderId && Object.keys(order.data || {}).length > 0) {
          initialData.previous_inspection = {
            inspection: {
              id: previousOrderId,
              name: order.data?.details?.building_id || previousOrderId,
            },
          };
        } else if (previousOrderFile) {
          initialData.previous_inspection = previousOrderFile;
        }
      }

      if (processedSchema.properties?.inspection_type && initialData.inspection_type === undefined) {
        initialData.inspection_type = processedSchema.properties.inspection_type.default;
      }

      // Auto-select single franchisee when applicable
      const franchiseeEnum = processedSchema.properties?.franchisees?.properties?.franchisee?.enum;
      const isNewTypeRestricted = CLIENT_RESTRICTED_FRANCHISEE_STEP_TYPES.includes(newOrderType);

      const shouldAutoSelectFranchisee =
        (franchiseeEnum?.length === 1 && !(isClient && isNewTypeRestricted)) ||
        (isClient && isSingleStepOrderType(newOrderType) && franchiseeEnum?.length > 0);

      if (shouldAutoSelectFranchisee && !initialData.franchisees?.franchisee) {
        initialData.franchisees = { franchisee: franchiseeEnum[0] };
      }

      if (isClient && me?.identifier && !initialData.clients?.client) {
        const clientPropType = processedSchema.properties?.clients?.properties?.client?.type;
        const formattedClientId =
          clientPropType === 'number' || clientPropType === 'integer'
            ? Number(me.identifier)
            : String(me.identifier);
        initialData.clients = { ...(initialData.clients || {}), client: formattedClientId };
      }

      setFormData(initialData);
      setInitialized(true);
    }
  }, [order, previousOrderId, previousOrderFile, initialized, isClient, newOrderType, processedSchema, me]);

  const steps = useMemo(() => {
    if (!processedSchema) return [];

    const isSingleStepOrder = isSingleStepOrderType(newOrderType);

    if (!isClient || isSingleStepOrder) return ['form'];

    const allSteps = [
      'franchisees',
      'details',
      'address',
      'parties_involved',
      'keys',
      'additionals',
      'accessInformation',
      'payment',
    ];

    let activeSteps = [...allSteps];

    const franchiseesKeys = processedSchema.properties?.franchisees?.properties?.franchisee?.enum || [];
    const isNewTypeRestricted = CLIENT_RESTRICTED_FRANCHISEE_STEP_TYPES.includes(newOrderType);

    if (isNewTypeRestricted || franchiseesKeys.length <= 1) {
      activeSteps = activeSteps.filter(s => s !== 'franchisees');
    }

    if (!me?.features?.includes('FORM_ACCESSINFO')) {
      activeSteps = activeSteps.filter(s => s !== 'accessInformation');
    }

    if (SKIP_CONFIRMATION_ORDER_TYPES.includes(newOrderType)) {
      activeSteps = activeSteps.filter(s => s !== 'payment');
    }

    if (newOrderType === 'tourVirtual') {
      activeSteps = activeSteps.filter(s => !['parties_involved', 'keys', 'additionals'].includes(s));
    }

    if (newOrderType === 'acompanhamentoObraMM') {
      const idx = activeSteps.indexOf('address');
      if (idx !== -1) {
        activeSteps.splice(idx, 0, 'construction_details', 'solicitations');
      }
      activeSteps = activeSteps.filter(s => !['additionals', 'accessInformation'].includes(s));
    }

    if (newOrderType === 'cacaVazamento') {
      activeSteps = activeSteps.filter(s => !['keys', 'accessInformation'].includes(s));
    }

    return activeSteps;
  }, [processedSchema, newOrderType, me, isClient]);

  const isSingleStepOrder = useMemo(() => {
    if (!processedSchema) return false;
    return isSingleStepOrderType(newOrderType);
  }, [processedSchema, newOrderType]);

  const showGenericWarning = isClient && (newOrderType === 'visitaDigital' || newOrderType === 'fotosDeCaptacao');

  const stepMapping = useMemo(() => {
    const additionalsFields = [
      'additionals',
      'recommendation',
      'reportSubmission',
      'vacationNotice',
      'budgetDuringInspection',
    ];

    // additionalCosts and cost suppressed for new client orders
    const showCosts = !isClient;
    if (showCosts) {
      additionalsFields.push('cost', 'additionalCosts');
    }
    additionalsFields.push('schedule');

    return {
      franchisees: ['franchisees'],
      details: ['inspection_type', 'previous_inspection', 'details', 'photoBuildingDetails', 'attachments', 'tags'],
      construction_details: ['construction_details'],
      solicitations: ['solicitations'],
      address: ['address'],
      parties_involved: ['parties_involved'],
      keys: ['keys'],
      additionals: additionalsFields,
      accessInformation: ['accessInformation', 'accompaniedInspection', 'tourAccessInfo'],
      form: null,
      payment: [],
    };
  }, [isClient]);

  const currentStepKey = useMemo(() => {
    if (!order) return null;
    const allSteps = steps;
    return allSteps[currentStep] || 'form';
  }, [order, currentStep, steps]);

  const getSchemaForStep = useCallback(
    (stepKey, fullSchema) => {
      if (!fullSchema || !stepKey) return fullSchema;
      if (stepKey === 'form' || !isClient) return fullSchema;
      if (stepKey === 'payment') return { ...fullSchema, properties: {}, required: [] };

      const keysForStep = stepMapping[stepKey] || [];
      const allKeys = Object.keys(fullSchema.properties || {});
      const props = {};

      keysForStep.forEach(k => {
        if (
          k === 'budgetDuringInspection' &&
          (!me?.features?.includes('FORM_BUDGET_DURING_INSPECTION') || newOrderType !== 'saida')
        ) {
          return;
        }

        if (allKeys.includes(k)) props[k] = fullSchema.properties[k];
      });

      return {
        ...fullSchema,
        title: stepKey,
        properties: props,
        required: (fullSchema.required || []).filter(r => keysForStep.includes(r)),
        dependencies: undefined,
      };
    },
    [me, newOrderType, stepMapping]
  );

  const stepSchema = useMemo(() => {
    return getSchemaForStep(currentStepKey, processedSchema);
  }, [currentStepKey, processedSchema, getSchemaForStep]);

  const uiSchema = useMemo(() => {
    if (!order) return {};
    const generatedUiSchema = generateUiSchema({
      isNewOrder: true,
      inspectionType: newOrderType,
      clientPanel: isClient,
      me,
      orderData: order?.data || {},
      status: !isClient && newOrderStatus !== 'SAVE_ORDER' ? 'DISABLED' : 'ACTIVE',
      schema: processedSchema,
      previousOrderId,
      previousOrderFile,
    });

    if (isClient && (!steps.includes('franchisees') || isSingleStepOrder)) {
      generatedUiSchema.franchisees = hiddenObjectField('franchisee');
    }

    // For franchisees (non-clients), always hide the franchisee selector field.
    // generateUiSchema already does this but we enforce it here as a safeguard.
    if (!isClient) {
      generatedUiSchema.franchisees = hiddenObjectField('franchisee');

      // Initialize price as editable for franchisees in new orders.
      // The franchisee sets the price ("a combinar"), and the orderprice API may
      // later lock it to STANDARD_METER. Without this, the field starts disabled
      // because uiSchemaNew.json sets ui:disabled:true, and the API is only
      // called after modality+urgency+area+buildingType+furnished are all filled.
      const isMarketplaceType = newOrderType?.startsWith('marketplace_');
      if (!isMarketplaceType && generatedUiSchema.details?.price) {
        generatedUiSchema.details = {
          ...generatedUiSchema.details,
          price: {
            ...generatedUiSchema.details.price,
            'ui:widget': 'customPriceWidget',
            'ui:disabled': false,
          },
        };
      }
    }

    return generatedUiSchema;
  }, [order, processedSchema, newOrderType, me, isClient, newOrderStatus, steps, isSingleStepOrder]);

  const formContext = useMemo(() => {
    const clientId = formData?.clients?.client || (isClient ? me?.identifier : null);

    return {
      clientId,
      me,
      isClient,
      currentStepKey,
      allFormData: formData,
      lastPriceParamsRef,
      setIsFileUploading,
      isFileUploading,
    };
  }, [formData, me, isClient, currentStepKey, isFileUploading]);

  const stepUiSchema = useMemo(() => {
    if (!uiSchema) return {};

    const baseSchema = stepSchema || processedSchema;
    if (!baseSchema) return {};

    if (!isClient || isSingleStepOrder || currentStepKey === 'form') {
      return sanitizeUiSchemaForSchema(uiSchema, baseSchema);
    }

    if (currentStepKey === 'payment') return {};

    const allowedKeys = Object.keys(stepSchema?.properties || {});
    const filteredUiSchema = {};

    allowedKeys.forEach(key => {
      if (uiSchema[key] !== undefined) {
        filteredUiSchema[key] = uiSchema[key];
      }
    });

    filteredUiSchema['ui:order'] = [...allowedKeys, '*'];

    return sanitizeUiSchemaForSchema(filteredUiSchema, stepSchema);
  }, [uiSchema, isClient, isSingleStepOrder, currentStepKey, stepSchema, processedSchema]);

  const stepFormData = useMemo(() => {
    if (!isClient || isSingleStepOrder || currentStepKey === 'form') return formData;

    const allowedKeys = Object.keys(stepSchema?.properties || {});
    const filteredData = {};

    allowedKeys.forEach(key => {
      if (formData[key] !== undefined) {
        filteredData[key] = formData[key];
      }
    });

    return filteredData;
  }, [formData, isClient, isSingleStepOrder, currentStepKey, stepSchema]);

  const handleNext = () => {
    if (isFileUploading) return;
    setSubmitted(true);
    if (formRef.current) {
      formRef.current.submitButton.click();
    }
  };

  const handleBack = () => {
    setErrors(0);
    setErrorMessage('');
    setSubmitted(false);
    setNewOrderStatus('SAVE_ORDER');
    setCurrentStep(prev => Math.max(0, prev - 1));
  };

  const handleFinalSubmit = async () => {
    if (isFileUploading) return;
    if (mutations.createOrder.isPending || mutations.saveOrder.isPending || mutations.packageConsume.isPending) {
      return;
    }

    const shouldCheckUnproductiveWarning =
      isClient && me?.features?.includes('FORM_ACCESSINFO') && !SKIP_CONFIRMATION_ORDER_TYPES.includes(newOrderType);

    if (shouldCheckUnproductiveWarning && !unproductiveWarningAccepted) {
      setSubmitted(true);
      return;
    }

    if (newOrderStatus === 'PURCHASE_PACKAGE' && selectedPackage) {
      try {
        setNewOrderStatus('WORKING');
        await mutations.purchasePackage.mutateAsync({
          packageId: selectedPackage,
          clientId: formData.clients?.client,
        });
        const res = await mutations.packageConsume.mutateAsync(formData);
        setPackageConsume(res);
        if (res.hasRequiredCredits) {
          await executeCreateOrder(formData);
        } else {
          setNewOrderStatus('PURCHASE_PACKAGE');
        }
      } catch (err) {
        console.debug('[OrderFormPage] Purchase error', err);
        setErrorMessage(err?.response?.data?.message || 'orderForm.purchaseError');
        setNewOrderStatus('PURCHASE_PACKAGE');
      }
      return;
    }

    await executeCreateOrder(formData);
  };

  const executeCreateOrder = async finalData => {
    try {
      if (!isClient) {
        setNewOrderStatus('WORKING');
      }
      const payload = {
        ...finalData,
        ...(order?.data?.source_order ? { source_order: order.data.source_order } : {}),
      };
      const res = await mutations.createOrder.mutateAsync(payload);
      const data = res.data ? { ...res.data } : { ...res };
      const orderId = data?.id || data?.order_id || data?.order?.id || data?.form?.id;
      const createdOrder = orderId ? { ...data, id: orderId } : data;

      const isBryOrder =
        data?.type === 'marketplace_bry' ||
        data?.form?.type === 'marketplace_bry' ||
        finalData?.type === 'marketplace_bry';

      if (isBryOrder && orderId) {
        const result = await getBryEnvelopeConfig({
          orderId,
          intl,
          dispatchToast: toast => dispatch(dispatchToastAction(toast)),
        });
        if (result?.success && result?.url) {
          dispatch(
            dispatchToastAction({
              type: 'success',
              title: intl.formatMessage({ id: 'orderDetails.bry.envelopeRegistered' }),
              text: intl.formatMessage({ id: 'orderDetails.bry.clickToConfigure' }),
              link: result.url,
            })
          );
        }
      }

      if (updateOrderItem && typeof updateOrderItem === 'function') {
        updateOrderItem(createdOrder);
      }
      orderCreationSucceededRef.current = true;
      successfulOrderCreation(createdOrder);
    } catch (err) {
      setErrorMessage(err?.response?.data?.message || 'UNKNOWN_ERROR');
      if (!isClient) {
        setNewOrderStatus('CONFIRM_ORDER');
      }
    }
  };

  const handleFormChange = useCallback(({ formData: nextFormData }) => {
    setFormData(prev => deepMerge(prev, nextFormData));
  }, []);

  const onStepSubmit = useCallback(
    async ({ formData: stepData }) => {
      const mergedData = deepMerge(formData, stepData);
      setFormData(mergedData);
      setSubmitted(false);
      setErrors(0);

      if (isSingleStepOrder) {
        if (showGenericWarning && !genericWarningAccepted) {
          setSubmitted(true);
          return;
        }
        await executeCreateOrder(mergedData);
        return;
      }

      if (isClient) {
        const nextStepIndex = currentStep + 1;
        const nextStepKey = steps[nextStepIndex];

        if (currentStep === steps.length - 1) {
          await handleFinalSubmit();
        } else if (nextStepKey === 'payment') {
          setCurrentStep(nextStepIndex);
          setNewOrderStatus('WORKING');
          try {
            const res = await mutations.packageConsume.mutateAsync(mergedData);
            setPackageConsume(res);
            if (res.hasRequiredCredits) {
              setNewOrderStatus('CONFIRM_ORDER');
            } else {
              setNewOrderStatus('PURCHASE_PACKAGE');
            }
          } catch (err) {
            console.debug('[OrderFormPage] Package consume error', err);
            setNewOrderStatus('SAVE_ORDER');
          }
        } else {
          setCurrentStep(nextStepIndex);
        }
      } else {
        const orderAction = ['docusign', 'visitaDigital', 'fotosDeCaptacao', 'fichacerta', 'captura360', 'cacaVazamento'].includes(
          mergedData.type
        )
          ? 'FINALIZE'
          : newOrderStatus;

        if (orderAction === 'SAVE_ORDER') {
          if (SKIP_CONFIRMATION_ORDER_TYPES.includes(newOrderType)) {
            await executeCreateOrder(mergedData);
            return;
          }

          setNewOrderStatus('WORKING');
          mutations.packageConsume
            .mutateAsync(mergedData)
            .then(res => {
              setPackageConsume(res);
              if (res.hasRequiredCredits) {
                setNewOrderStatus('CONFIRM_ORDER');
              } else {
                setNewOrderStatus('PURCHASE_PACKAGE');
              }
            })
            .catch(err => {
              console.debug('[OrderFormPage] Franchisee package consume error', err);
              setNewOrderStatus('SAVE_ORDER');
            });
        } else {
          // CONFIRM_ORDER, PURCHASE_PACKAGE (with selected package), or FINALIZE
          await executeCreateOrder(mergedData);
        }
      }

      window.scrollTo(0, 0);
    },
    [
      formData,
      isClient,
      isSingleStepOrder,
      showGenericWarning,
      genericWarningAccepted,
      executeCreateOrder,
      mutations.packageConsume,
      newOrderStatus,
      currentStep,
      steps,
      handleFinalSubmit,
    ]
  );

  const handleCancelFlow = () => {
    const confMessage = intl.formatMessage({ id: 'confirmation.cancelNewOrder' });
    if (window.confirm(confMessage)) {
      cancelNewOrder();
      navigate('/orders');
    }
  };

  const isSaving =
    mutations.createOrder.isPending ||
    mutations.packageConsume.isPending ||
    mutations.purchasePackage.isPending ||
    newOrderStatus === 'WORKING';
  const isPaymentStep = isClient && currentStepKey === 'payment';
  if (isQueryLoading) return <Loading />;
  if (!order) return null;

  return (
    <ApiContext.Provider value={{ status: isSaving ? 'WORKING' : 'IDLE' }}>
      <div className={`${isClient ? '' : 'mx-[1vw]'} bg-white pb-[0.5vw] text-[14px]`} ref={scrollContainerRef}>
        {!isClient && (
          <OrderFormPageHeader
            inspectionIcon={
              inspectionIcon || (newOrderType && <OrderIcon orderType={newOrderType} fontSize="calc(16px + 0.8vw)" />)
            }
            text="orderForm.newOrder"
          />
        )}

        <main className="w-full">
          {isPaymentStep ? (
            <div className="px-[2vw] py-[2vh] space-y-8">
              {me?.features?.includes('FORM_ACCESSINFO') && (
                <UnproductiveWarning
                  onChange={setUnproductiveWarningAccepted}
                  submitted={submitted && !unproductiveWarningAccepted}
                />
              )}

              {newOrderStatus === 'PURCHASE_PACKAGE' ? (
                <NoAvailablePackages
                  data={packageConsume}
                  handleSelectPackage={e => setSelectedPackage(e.target.value)}
                  selectedPackage={selectedPackage}
                />
              ) : (
                <PackageConsumption data={packageConsume} />
              )}
            </div>
          ) : (
            <div
              className={`w-full font-['Open_Sans'] font-bold ${isClient ? '' : 'px-[1vw]'} ${
                containerClasses.orderFormContainer
              }`}
            >
              <OrderForm
                key={isClient ? `step-${currentStepKey}` : 'franchisee'}
                ref={formRef}
                formData={stepFormData}
                schema={stepSchema}
                uiSchema={stepUiSchema}
                handleError={setErrors}
                onSubmit={onStepSubmit}
                onChange={handleFormChange}
                submitted={submitted}
                orderType={newOrderType}
                previousOrderFile={previousOrderFile}
                isNewOrder={true}
                formContext={formContext}
              />
              {showGenericWarning && (
                <div className="px-[1vw] py-[2vh]">
                  <GenericWarning
                    onChange={setGenericWarningAccepted}
                    submitted={submitted && !genericWarningAccepted}
                  />
                </div>
              )}
            </div>
          )}
        </main>

        <div className="w-full mt-4">
          <OrderFormPageFooter
            disabledSecondButton={isFileUploading}
            handleFirstButton={
              newOrderStatus !== 'SAVE_ORDER' && !isClient
                ? () => setNewOrderStatus('SAVE_ORDER')
                : currentStep === 0
                ? handleCancelFlow
                : handleBack
            }
            handleSecondButton={
              isPaymentStep || (!isClient && newOrderStatus !== 'SAVE_ORDER') ? handleFinalSubmit : handleNext
            }
            textFirstButton={
              newOrderStatus !== 'SAVE_ORDER' && !isClient
                ? 'BUTTON.BACK'
                : currentStep === 0
                ? 'BUTTON.CANCEL'
                : 'BUTTON.BACK'
            }
            textSecondButton={
              // Franchisees always use a single 'form' step, so always show SAVE/CONFIRM/PURCHASE.
              // Clients on non-last step show NEXT; clients on last step or payment show SAVE/CONFIRM.
              isPaymentStep || (!isClient && newOrderStatus !== 'SAVE_ORDER') || isSingleStepOrder || !isClient
                ? newOrderStatus === 'PURCHASE_PACKAGE'
                  ? 'BUTTON.PURCHASE'
                  : 'BUTTON.SAVE'
                : currentStep < steps.length - 1
                ? 'BUTTON.NEXT'
                : 'BUTTON.SAVE'
            }
          >
            {!isPaymentStep && newOrderStatus === 'PURCHASE_PACKAGE' && (
              <NoAvailablePackages
                data={packageConsume}
                handleSelectPackage={e => setSelectedPackage(e.target.value)}
                selectedPackage={selectedPackage}
              />
            )}
            {!isPaymentStep && newOrderStatus === 'CONFIRM_ORDER' && <PackageConsumption data={packageConsume} />}

            {(errors > 0 || errorMessage) && (
              <FormError>
                {errorMessage ? (
                  <FormattedMessage
                    id={errorMessage}
                    values={{
                      a: chunks => (
                        <a
                          href={`/marketplace/docusign#access_token=${window.sessionStorage.getItem(
                            'accessToken'
                          )}&token_type=${window.sessionStorage.getItem('tokenType')}&expires_in=`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="underline text-blue-600 hover:text-blue-800"
                        >
                          {chunks}
                        </a>
                      ),
                    }}
                  />
                ) : (
                  <FormattedMessage id="orderForm.footerError" values={{ errors }} />
                )}
              </FormError>
            )}
          </OrderFormPageFooter>
        </div>
      </div>
    </ApiContext.Provider>
  );
};

OrderFormPage.propTypes = {
  cancelNewOrder: PropTypes.func.isRequired,
  inspectionIcon: PropTypes.object,
  newOrderType: PropTypes.string.isRequired,
  previousOrderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  previousOrderFile: PropTypes.object,
  previousPropertyId: PropTypes.string,
  successfulOrderCreation: PropTypes.func.isRequired,
  updateOrderItem: PropTypes.func,
};

OrderFormPage.defaultProps = {
  inspectionIcon: null,
  previousOrderFile: null,
  previousOrderId: null,
  previousPropertyId: null,
  updateOrderItem: null,
};

export default OrderFormPage;
